import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

import { SnAbilityService } from '@lib/ability';
import { SnWidget } from '@lib/api/entities';
import { SnOperationalViewsAPI } from '@lib/api/services';
import { SnFormFieldConfig } from '@lib/form';
import { t } from '@lib/translation';

@Component({
  selector: 'sn-embed-youtube-widget',
  templateUrl: './embed-youtube-widget.component.html',
  styleUrls: ['./embed-youtube-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmbedYoutubeWidgetComponent implements OnInit, OnDestroy {
  public url: SafeUrl;

  public changeUrl: boolean;

  public changeTitle = false;

  @Input() public widget: SnWidget;

  @Input() public viewId: string;

  public form: UntypedFormGroup = new UntypedFormGroup({});

  public formModel: any = {};

  public formFields: SnFormFieldConfig[];
  public readonly canChange: boolean;
  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _operationalViewsAPI: SnOperationalViewsAPI,
    private readonly _cd: ChangeDetectorRef,
    private readonly _sanitizer: DomSanitizer,
    private readonly _ability: SnAbilityService,
  ) {
    this.canChange = this._ability.can({ subject: 'dashboards/widgetobject', action: 'change' });
  }

  public ngOnInit(): void {
    this._initFields();
    this._checkForUrl();
    this._cd.detectChanges();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public submitForm(): void {
    if (!this.changeTitle) {
      const url = this._getYoutubeEmbedUrl(this.formModel.embedUrl);
      this._operationalViewsAPI
        .updateViewWidget(this.viewId, this.widget.id, { meta: { embedUrl: url } })
        .pipe(takeUntil(this._destroy$))
        .subscribe();
      this.widget.meta.embedUrl = url;
      this._checkForUrl();
      this._cd.detectChanges();
    } else {
      this.changeTitle = false;
      this._cd.detectChanges();
    }
  }

  public toogleChangeUrl(): void {
    this.changeUrl = !this.changeUrl;
    this._cd.detectChanges();
  }

  public editTitle(): void {
    this.changeTitle = true;
  }

  public setTitle(value: boolean): void {
    this.changeTitle = value;
    this._cd.detectChanges();
  }

  public setUpdateTitle(value: string): void {
    this.widget.name = value;
    this._cd.detectChanges();
  }

  private _getYoutubeEmbedUrl(url: string): string {
    // eslint-disable-next-line no-useless-escape
    const regExp = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    if (match && match[2].length === 11) {
      return `https://youtube.com/embed/${match[2]}`;
    }
    return url;
  }

  private _checkForUrl(): void {
    if (this.widget.meta.embedUrl) {
      this.url = this._sanitizer.bypassSecurityTrustResourceUrl(this.widget.meta.embedUrl);
      this.changeUrl = false;
      this._cd.detectChanges();
    }
  }

  private _initFields(): void {
    this.formFields = [
      {
        key: 'embedUrl',
        type: 'input',
        templateOptions: {
          appearance: 'outline',
          floatLabel: 'always',
          placeholder: t('dashboard.operational.widgets.youtubeUrlPlaceholder'),
          required: true,
          label: t('dashboard.operational.widgets.youtubeUrlLabel'),
        },
      },
    ];
  }
}
